import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { persistMessage, loadChatHistory } from '../../../services/chatService';
import { getSession } from '../../../api';

const { FiX, FiSend, FiMessageCircle, FiSmile, FiRefreshCw, FiDatabase } = FiIcons;

const AVATAR = (name = '?') =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=1E3A5F&color=C9A84C&bold=true&size=64`;

const SEED_MESSAGES = (name) => [
  { id: 'seed-1', from: 'them', text: `Hi! It's wonderful to be connected through the memorial. 🕯️`, time: '2d ago' },
  { id: 'seed-2', from: 'me',   text: `So glad we found each other here. How are you doing?`,          time: '2d ago' },
  { id: 'seed-3', from: 'them', text: `Doing well, thank you. It means a lot to share these memories.`, time: '1d ago' },
];

const TYPING_REPLIES = [
  `Thanks for reaching out! I'll get back to you soon. 💛`,
  `That means a lot to hear. Thinking of you too. 🕯️`,
  `So wonderful to be connected through this memorial.`,
  `I appreciate you messaging me. Let's stay in touch! 🌿`,
];

const InAppChatModal = ({ node, onClose }) => {
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState('');
  const [loading,   setLoading]   = useState(true);
  const [sending,   setSending]   = useState(false);
  const [isTyping,  setIsTyping]  = useState(false);
  const bottomRef = useRef(null);
  const isLoggedIn = !!getSession();

  // Load history on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const history = isLoggedIn ? await loadChatHistory(node?.id) : [];
      if (!cancelled) {
        // Merge seeds + real history, deduplicate by id
        const seeds   = SEED_MESSAGES(node?.name);
        const realIds = new Set(history.map(m => m.id));
        const merged  = [...seeds.filter(s => !realIds.has(s.id)), ...history];
        setMessages(merged);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [node?.id, isLoggedIn]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;

    const localId  = `local-${Date.now()}`;
    const optimistic = { id: localId, from: 'me', text, time: 'Just now' };
    setMessages(prev => [...prev, optimistic]);
    setInput('');
    setSending(true);

    // Persist to DB + fire notification (fire-and-forget)
    if (isLoggedIn) {
      persistMessage({ nodeId: node?.id, nodeName: node?.name, text }).catch(() => {});
    }

    // Simulate typing indicator then reply
    setTimeout(() => {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setSending(false);
        const reply = TYPING_REPLIES[Math.floor(Math.random() * TYPING_REPLIES.length)];
        setMessages(prev => [...prev, {
          id:   `reply-${Date.now()}`,
          from: 'them',
          text: reply,
          time: 'Just now',
        }]);
      }, 1400);
    }, 400);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        />

        {/* Chat window */}
        <motion.div
          className="relative w-full max-w-md bg-white rounded-[28px] shadow-2xl overflow-hidden flex flex-col"
          style={{ height: 560 }}
          initial={{ opacity: 0, scale: 0.92, y: 24 }}
          animate={{ opacity: 1, scale: 1,    y: 0  }}
          exit={{   opacity: 0, scale: 0.92, y: 24  }}
          transition={{ type: 'spring', stiffness: 340, damping: 28 }}
        >
          {/* Header */}
          <div className="flex items-center gap-4 px-6 py-4 bg-accent-navy text-white shrink-0">
            <div className="relative">
              <img src={AVATAR(node?.name)} alt={node?.name}
                className="w-10 h-10 rounded-xl object-cover border-2 border-white/20" />
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-accent-navy" />
            </div>
            <div className="flex-grow min-w-0">
              <p className="font-bold text-sm truncate">{node?.name}</p>
              <p className="text-[9px] uppercase tracking-widest text-white/50 font-black">{node?.relation}</p>
            </div>
            <div className="flex items-center gap-2">
              {/* DB badge */}
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/10 text-[8px] font-black uppercase tracking-widest text-white/60">
                <SafeIcon icon={FiDatabase} className="w-2.5 h-2.5" />
                Logged
              </div>
              <SafeIcon icon={FiMessageCircle} className="w-4 h-4 text-accent-gold" />
              <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-grow overflow-y-auto px-5 py-4 space-y-3 bg-gradient-to-b from-brand-light/30 to-white">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <SafeIcon icon={FiRefreshCw} className="w-6 h-6 text-text-muted animate-spin" />
              </div>
            ) : (
              <>
                {messages.map(msg => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.from === 'me' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.from === 'them' && (
                      <img src={AVATAR(node?.name)} alt=""
                        className="w-7 h-7 rounded-lg object-cover mr-2 mt-1 shrink-0 border border-brand-dark" />
                    )}
                    <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                      msg.from === 'me'
                        ? 'bg-accent-navy text-white rounded-br-md'
                        : 'bg-white text-accent-navy border border-brand-dark rounded-bl-md'
                    }`}>
                      {msg.text}
                      <p className={`text-[8px] mt-1 font-black uppercase tracking-widest ${
                        msg.from === 'me' ? 'text-white/40' : 'text-text-muted'
                      }`}>{msg.time}</p>
                    </div>
                  </motion.div>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start items-end gap-2">
                    <img src={AVATAR(node?.name)} alt=""
                      className="w-7 h-7 rounded-lg object-cover shrink-0 border border-brand-dark" />
                    <div className="bg-white border border-brand-dark rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5 items-center shadow-sm">
                      {[0, 1, 2].map(i => (
                        <motion.span key={i}
                          className="w-1.5 h-1.5 bg-text-muted rounded-full"
                          animate={{ y: [0, -4, 0] }}
                          transition={{ repeat: Infinity, duration: 0.7, delay: i * 0.15 }}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </>
            )}
            <div ref={bottomRef} />
          </div>

          {/* DB notice */}
          {isLoggedIn && (
            <div className="px-5 py-1.5 bg-teal-50 border-t border-teal-100 text-[9px] text-teal-700 font-black uppercase tracking-widest flex items-center gap-1.5">
              <SafeIcon icon={FiDatabase} className="w-2.5 h-2.5" />
              Messages are saved & logged to your notification center
            </div>
          )}

          {/* Input */}
          <div className="px-4 py-4 border-t border-brand-dark bg-white shrink-0 flex items-end gap-3">
            <button className="text-text-muted hover:text-accent-gold transition-colors pb-2">
              <SafeIcon icon={FiSmile} className="w-5 h-5" />
            </button>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              rows={1}
              placeholder={`Message ${node?.name}…`}
              className="flex-grow resize-none bg-brand-light border-2 border-brand-dark rounded-2xl px-4 py-2.5 text-sm outline-none focus:border-accent-gold transition-colors leading-relaxed"
              style={{ maxHeight: 80 }}
            />
            <button
              onClick={send}
              disabled={!input.trim() || sending}
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all disabled:opacity-30"
              style={{ background: input.trim() && !sending ? '#1E3A5F' : '#E5D9C3' }}
            >
              {sending
                ? <SafeIcon icon={FiRefreshCw} className="w-4 h-4 text-white animate-spin" />
                : <SafeIcon icon={FiSend}       className="w-4 h-4 text-white" />
              }
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InAppChatModal;